/**
 * Presenting an hour.
 *
 * Built for the one situation it is used in: a teacher at the front of a room, on a
 * projector, with a class waiting. That decides everything here — arrow keys and nothing to
 * hunt for with a mouse, the current block always named so they know where they are, and
 * notes that are theirs alone and never on the screen behind them.
 *
 * Nothing animates. A slide that moves is a slide a teacher cannot talk over.
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '../../lib/useQuery';
import { DesignShell, ErrorNote, Loading, S } from '../../design/kit';
import { SlideView } from '../../components/slides/SlideView';
import type { SlideElement, SlideLayoutName } from '../../components/slides/types';

interface DeckSlide {
  id: string;
  title: string;
  notes: string;
  elements: SlideElement[];
  blockId: string;
  blockTitle: string;
  layout?: SlideLayoutName;
}

interface Deck {
  event: { id: string; title: string; objective: string };
  subject: string;
  grade: string;
  curriculumName: string;
  blocks: { id: string; title: string; order: number; slideCount: number }[];
  slides: DeckSlide[];
}

export function PresentHour() {
  const { id = '', eventId = '' } = useParams();
  const { data, loading, error, reload } = useQuery<Deck>(`/unit-plans/${id}/events/${eventId}/deck`);
  const [at, setAt] = useState(0);
  const [showNotes, setShowNotes] = useState(true);

  const total = data?.slides.length ?? 0;
  const go = useCallback((delta: number) => setAt((n) => Math.min(Math.max(n + delta, 0), Math.max(0, total - 1))), [total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft' || e.key === 'PageUp') { e.preventDefault(); go(-1); }
      else if (e.key === 'Home') setAt(0);
      else if (e.key === 'End') setAt(Math.max(0, total - 1));
      else if (e.key.toLowerCase() === 'n') setShowNotes((v) => !v);
      else if (e.key.toLowerCase() === 'f') void document.documentElement.requestFullscreen?.().catch(() => {});
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, total]);

  if (loading && !data) return <DesignShell title="Presenting"><Loading /></DesignShell>;
  if (error || !data) return <DesignShell title="Presenting"><ErrorNote error={error} retry={reload} /></DesignShell>;

  if (total === 0) {
    return (
      <DesignShell title={data.event.title}>
        <div style={{ ...S.card, textAlign: 'center', padding: '3rem 1rem' }}>
          <p style={{ fontSize: '1.05rem', margin: 0 }}>This hour has no slides yet.</p>
          <p style={S.mutedSm}>Build the slides for a block and they appear here, in block order.</p>
        </div>
      </DesignShell>
    );
  }

  const slide = data.slides[Math.min(at, total - 1)]!;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--c-bg, #fff)' }}>
      {/* A thin rail, not a header: the class is looking at this too. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '10px 20px', borderBottom: '1px solid var(--c-rule, rgba(17,20,28,.1))', fontSize: '.8125rem', color: 'var(--c-ink-3)' }}>
        <strong style={{ color: 'var(--c-ink)' }}>{data.event.title}</strong>
        <span>{data.curriculumName} · {data.subject} · {data.grade}</span>
        <span style={{ marginLeft: 'auto' }}>{slide.blockTitle}</span>
        <span aria-live="polite">{at + 1} / {total}</span>
      </div>

      {/*
        The slide takes the whole frame. It used to sit in a 62rem box centred in it, which is
        why a three-bullet slide left two thirds of the projection empty.
      */}
      <main style={{ flex: 1, minHeight: 0, overflow: 'hidden' }} aria-label={`Slide ${at + 1} of ${total}`}>
        <SlideView slide={slide} layout={slide.layout} subject={data.subject} eyebrow={slide.blockTitle} />
      </main>

      {showNotes && slide.notes ? (
        <aside
          style={{ borderTop: '1px solid var(--c-rule, rgba(17,20,28,.1))', background: 'var(--c-surface-2, #f6f5f2)', padding: '12px 20px', fontSize: '.95rem', lineHeight: 1.55, maxHeight: '22vh', overflow: 'auto' }}
          aria-label="Speaker notes"
        >
          <span style={{ ...S.mutedXs, fontWeight: 650, textTransform: 'uppercase', letterSpacing: '.07em' }}>Your notes</span>
          <p style={{ margin: '.35rem 0 0' }}>{slide.notes}</p>
        </aside>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 20px', borderTop: '1px solid var(--c-rule, rgba(17,20,28,.1))' }}>
        <button type="button" className="btn" onClick={() => go(-1)} disabled={at === 0} style={{ minHeight: '44px', minWidth: '44px' }}>← Back</button>
        <button type="button" className="btn" onClick={() => go(1)} disabled={at >= total - 1} style={{ minHeight: '44px', minWidth: '44px' }}>Next →</button>
        <button type="button" className="btn" onClick={() => setShowNotes((v) => !v)} style={{ minHeight: '44px' }} aria-pressed={showNotes}>
          {showNotes ? 'Hide notes' : 'Show notes'}
        </button>
        <span style={{ ...S.mutedXs, marginLeft: 'auto' }}>← → to move · N for notes · F for full screen</span>
      </div>
    </div>
  );
}
